import React, { useEffect, useState } from "react";
import { interesesCortos } from "../../../utils/cursoCorto";

import HttpManager from "../../../services/HttpManager";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { Campaign, CampaignContact, ProspectCatalogs } from "../../../services/v2/prospects.service";
import { cuando, errorTexto, nombreCompleto, statusClase } from "./salesHelpers";
import ClienteView from "./clienteView";
import { useTelefono } from "./telefono/telefonoUI";

interface Props {
	campusId: number;
	catalogs: ProspectCatalogs | null;
	onSalir: () => void;
}

/**
 * Llamar una campaña (Herramientas). Desde el 2026-10-14 la lista diaria de la asesora vive en el panel del Marcador
 * (píldora de la barra, cola por niveles en el servidor); aquí quedan solo las campañas que arma supervisión: la asesora
 * elige una y le va tocando "la siguiente" con candado (dos asesoras nunca reciben a la misma).
 */
const MarcadorView: React.FC<Props> = ({ campusId, catalogs, onSalir }) => {
	const http = HttpManager.getInstance();
	const [error, setError] = useState<string | null>(null);
	const [cargadas, setCargadas] = useState<boolean>(false);
	// Campañas (las arma supervisión): la asesora elige una y le va tocando "la siguiente".
	const tel = useTelefono();
	const [campanas, setCampanas] = useState<Campaign[]>([]);
	const [campana, setCampana] = useState<Campaign | null>(null);
	const [contacto, setContacto] = useState<CampaignContact | null>(null);
	const [hechasC, setHechasC] = useState<{ nombre: string; res: string }[]>([]);
	const [espera, setEspera] = useState<string | null>(null);
	useEffect(() => {
		http.prospectsService.campaigns(campusId).then((r) => setCampanas(r.data.campaigns.filter((c) => c.status === "activa" && c.soyAgente))).catch(() => setCampanas([]))
			.finally(() => setCargadas(true));
	}, [campusId]);
	const siguienteC = async (c: Campaign) => {
		setError(null); setEspera(null);
		try {
			const r = await http.prospectsService.campaignNext(campusId, c.id);
			setContacto(r.data.contact);
			if (!r.data.contact) setEspera(r.data.proximoReintento ? "Por ahora nadie. Vuelven a salir desde " + cuando(r.data.proximoReintento) + "." : "Terminaste tu parte de la campaña.");
		} catch (e) { setContacto(null); setError(errorTexto(e)); }
	};
	const saltarC = async () => {
		if (!campana || !contacto) return;
		try { await http.prospectsService.campaignSkip(campusId, contacto.id); } catch (e) { /* igual pasa a la siguiente */ }
		siguienteC(campana);
	};

	if (campana) {
		const automatico = campana.pacing === "automatico";
		return (
			<div className="ventas-cuerpo marcador">
				<div className="mc-cola">
					<div className="mc-cab">
						<div><b>{campana.name}</b><span className="apagado"> · {campana.mode === "grupal" ? "fila del equipo" : "tu gente"} · {automatico ? "automático" : "vista previa"}</span></div>
						<button className="enlace" onClick={() => { setCampana(null); setContacto(null); setHechasC([]); }}>Salir de la campaña</button>
					</div>
					<div className="mc-acciones">
						{!contacto ? <button className="btn-wa pri" onClick={() => siguienteC(campana)}>{hechasC.length ? "Siguiente" : "Empezar"}</button> : <button className="btn-wa" onClick={saltarC}>Saltar</button>}
						{automatico && !tel.listo ? <span className="oro" style={{ fontSize: 12 }}>Conectando el teléfono…</span> : null}
					</div>
					{error ? <div className="aviso error" style={{ margin: 10 }}>{error}</div> : null}
					{espera ? <div className="aviso" style={{ margin: 10 }}>{espera}</div> : null}
					<div className="filas">
						{contacto ? (
							<div className="ventas-fila activa">
								<div className="l1"><span className="nombre">{nombreCompleto(contacto.prospect)}</span><span className="cuando">intento {contacto.attempts + 1}</span></div>
								<div className="l2">{[telefonoBonito(contacto.prospect.phone_e164), interesesCortos(contacto.prospect.course_interest_text).join(", ")].filter(Boolean).join(" · ")}</div>
								<div className="chips"><span className={"chip " + statusClase(contacto.prospect.status_id)}>{contacto.prospect.status}</span></div>
							</div>
						) : null}
						{hechasC.slice().reverse().map((h, i) => <div key={i} className="ventas-fila hecha"><div className="l1"><span className="nombre">{h.nombre}</span><span className="cuando">{h.res.replace("_", " ")}</span></div></div>)}
					</div>
				</div>
				{contacto ? (
					<ClienteView key={contacto.id} campusId={campusId} prospectId={contacto.prospectId} catalogs={catalogs} onChanged={() => { /* la campaña lleva la cuenta */ }}
						onOpen={() => { /* en campaña se queda en la persona */ }} onBack={() => setContacto(null)}
						marcador={{ autoLlamar: automatico, campaignContactId: contacto.id, campaignId: campana.id, callerId: campana.caller_id, onResultado: (res) => {
							setHechasC((h) => [...h, { nombre: nombreCompleto(contacto.prospect), res }]); setContacto(null); siguienteC(campana);
						} }} />
				) : (
					<div className="ventas-cliente"><div className="ventas-vacio mc-inicio">
						<h3>{campana.name}</h3>
						<p>{automatico ? "Al terminar cada llamada y guardar cómo terminó, marca sola a la siguiente." : "Te sale la ficha de la siguiente persona; revisa y dale Llamar."}</p>
						<button className="btn-wa pri" onClick={() => siguienteC(campana)}>{hechasC.length ? "Siguiente" : "Empezar"}</button>
					</div></div>
				)}
			</div>
		);
	}

	return (
		<div className="ventas-cuerpo marcador">
			<div className="ventas-cliente">
				<div className="ventas-vacio mc-inicio">
					<h3>Campañas de llamadas</h3>
					<p>Tu lista de llamadas de hoy está en el botón <b>Marcador</b> de arriba. Aquí van las campañas que arma supervisión.</p>
					{error ? <div className="aviso error">{error}</div> : null}
					{campanas.length ? (
						<div className="mc-campanas">
							{campanas.map((c) => <button key={c.id} className="btn-wa" onClick={() => { setCampana(c); setContacto(null); setHechasC([]); setEspera(null); }}>{c.name}</button>)}
						</div>
					) : <p className="apagado">{cargadas ? "No tienes campañas activas ahora." : "Cargando…"}</p>}
					<button className="btn-wa" onClick={onSalir}>Volver</button>
				</div>
			</div>
		</div>
	);
};

export default MarcadorView;
